import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import {
  PrerequisiteLink,
  PrerequisiteService,
} from '../../../services/prerequisite.service';

/**
 * Eine Kursverknüpfung bestätigen oder ablehnen — direkt aus der
 * Benachrichtigung, ohne Umweg über eine Seite.
 *
 * Vorher sprang die Meldung nur in die Moodle-Übersicht und öffnete dort die
 * Verknüpfungen des Kurses; welcher Eintrag gemeint war, musste man selbst
 * suchen. Eine Meldung, die „bestätigen" heißt, muss das Bestätigen auch
 * anbieten. Dasselbe Muster wie `LinkRequestDecisionDialogComponent` beim
 * Twin↔Tutor-Antrag.
 *
 * Einen eigenen Endpunkt braucht es dafür nicht: die Benachrichtigung trägt
 * `reference_id` (die Verknüpfung) und `target_course_id` in der Metadata, und
 * über den Zielkurs kommt die Liste, aus der die eine Zeile herausfällt.
 */
@Component({
  selector: 'app-prerequisite-decision-dialog',
  templateUrl: './prerequisite-decision-dialog.component.html',
  styleUrl: './prerequisite-decision-dialog.component.scss',
})
export class PrerequisiteDecisionDialogComponent implements OnInit {
  @Input() linkId!: string;
  @Input() targetCourseId!: string;

  loading = true;
  busy = false;
  link: PrerequisiteLink | null = null;
  /** Gesetzt, wenn es die Verknüpfung nicht (mehr) gibt. */
  notFound = false;

  constructor(
    protected dialogRef: NbDialogRef<PrerequisiteDecisionDialogComponent>,
    private service: PrerequisiteService,
    private toastr: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    if (!this.targetCourseId || !this.linkId) {
      this.loading = false;
      this.notFound = true;
      return;
    }
    this.loading = true;
    this.service.list(this.targetCourseId).subscribe({
      next: (links) => {
        this.link = links.find((l) => l.id === this.linkId) || null;
        this.notFound = !this.link;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
      },
    });
  }

  get statusLabel(): string {
    switch (this.link?.status) {
      case 'active':
        return 'Freigegeben';
      case 'pending':
        return 'Wartet auf Bestätigung';
      case 'rejected':
        return 'Abgelehnt';
      case 'revoked':
        return 'Widerrufen';
      default:
        return this.link?.status || '';
    }
  }

  /** Nur wer selbst noch aussteht, sieht die normalen Knöpfe. */
  get canDecide(): boolean {
    return this.link?.my_state === 'pending' && this.link?.status === 'pending';
  }

  /** Managerinnen und Admins dürfen zusätzlich für alle entscheiden. Getrennt
   *  vom normalen Knopf, damit ein Admin, der selbst verantwortlich ist, nicht
   *  versehentlich auch die Antwort der anderen schließt. */
  get canOverride(): boolean {
    return !!this.link?.can_override && this.link?.status === 'pending';
  }

  decide(grant: boolean, override = false): void {
    if (!this.link || this.busy) return;
    this.busy = true;
    this.service.decide(this.link.id, grant, undefined, override).subscribe({
      next: (r) => {
        this.busy = false;
        this.toastr.success(
          r.status === 'active'
            ? (override
                ? 'Für alle bestätigt — die Verknüpfung ist jetzt aktiv. Im Protokoll steht, dass die Verwaltung entschieden hat.'
                : 'Bestätigt — die Verknüpfung ist jetzt aktiv.')
            : grant
            ? 'Bestätigt. Es fehlen noch weitere Zustimmungen.'
            : 'Abgelehnt. Die Verknüpfung bleibt geschlossen.',
          'Kursverknüpfung',
        );
        this.dialogRef.close(true);
      },
      error: (e) => {
        this.busy = false;
        this.toastr.danger(e?.error?.detail || 'Nicht möglich', 'Kursverknüpfung');
      },
    });
  }
}
